import React, { useState, useEffect, useMemo } from 'react';
import { securePut, secureGetPdf, api, getResponseData } from '../../config/api';
import { getJwkKeystore } from '../../utils/encryption';
import { Helmet } from "react-helmet-async";
import { useTranslation } from 'react-i18next';

import { formatAmount } from '../../utils/format';
import { SaveCancelTop } from '../../components/PrimaryButton';
import Input from '../../components/Input';
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from "../../components/Breadcrumbs";
import PdfDropzone from '../../components/PdfDropzone';
import { CONSUMPTION_UNITS, consumptionUnitLabel, isPeintureProduct, unitAcceptsQuantity } from '../../utils/paint';

import plusIcon from '../../icons/plus.svg';
import deleteIcon from '../../icons/trashR.svg';
import plus from '../../icons/plusP.svg';
import minus from '../../icons/minusP.svg';
import noImage from '../../icons/noImageIcon.svg';


const LINE_TYPES = [
  { value: 'PIECES', label: 'Pièces' },
  { value: 'PEINTURE', label: 'Peinture et ingrédients' },
];
// No line type is preselected: the other fields appear once one is chosen
const EMPTY_LINE = { lineType: '', productName: '', quantity: '', unitPrice: '', consumptionUnit: '', packageContent: '' };
const unitLabel = consumptionUnitLabel;
// Once saved, only the BL number, the reception date and the PDFs of a purchase can change
const EDITABLE_FIELDS = ['receptionDate', 'factureNumber'];

// Greys out a field that can't be changed in edit mode
const Grayed = ({ active, children }) => (
  active ? <div className="opacity-50 pointer-events-none">{children}</div> : children
);

const toListProduct = ({ product, price, quantity, consumptionUnit, packageContent }) => ({
  reference: product?.reference,
  productName: product?.name,
  id: product?.id,
  quantity,
  unitPrice: Number(price).toFixed(2),
  image: product?.image,
  ...(packageContent != null && { consumptionUnit, packageContent }),
});

const PurchaseForm = ({
  title,
  mode = 'add',
  initialValues = {},
  baseFields = [],
  listFields = [],
  products = [],
  onSubmit,
  onSuccess,
  onCancel,
}) => {
  const [formValues, setFormValues] = useState({});
  const isModify = mode === 'modify';
  // Only a stock entry whose invoice is still "À vérifier" can be edited
  const locked = isModify && initialValues.status !== 'TO_VERIFY';
  const [productList, setProductList] = useState(() => (isModify ? (initialValues.products || []).map(toListProduct) : []));
  const [currentProduct, setCurrentProduct] = useState(EMPTY_LINE);
  const isPaintLine = currentProduct.lineType === 'PEINTURE';
  const isPiecesLine = currentProduct.lineType === 'PIECES';
  // A paint product's unit is fixed by its first purchase
  const lockedUnit = isPaintLine
    ? products.find((product) => product.value === currentProduct.productName)?.fullEntity?.consumptionUnit
    : null;
  // Supplier invoice (optional) and delivery note (required), uploaded on submit
  const [files, setFiles] = useState({ invoice: null, deliveryNote: null });
  const [submitting, setSubmitting] = useState(false);
  // PDFs already attached to the purchase being edited - the stored names are generated, so they get a readable one
  const existingFiles = useMemo(() => {
    if (!isModify) return {};
    const existing = (path, prefix) => ({
      name: `${prefix}-${initialValues.purchaseNumber}.pdf`,
      getPdfBlob: async () => (await secureGetPdf(`/purchases/${initialValues.id}/${path}/pdf`))?.data,
    });
    return {
      deliveryNote: initialValues.deliveryNoteFileUrl && existing('delivery-note', 'BL'),
      invoice: initialValues.invoiceFileUrl && existing('invoice', 'Facture'),
    };
  }, [isModify, initialValues]);
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    const initialValues = {};
    baseFields.forEach((field) => {
      initialValues[field.name] = field.value || '';
    });
    setFormValues(initialValues);
  }, [baseFields]);

  const handleInputChange = (name, value) => {
    setFormValues((prevValues) => ({ ...prevValues, [name]: value }));
  };

  const handleListFieldChange = (name, value) => {
    setCurrentProduct((prevProduct) => {
      const next = { ...prevProduct, [name]: value };
      if (name === 'productName') {
        const product = products.find((p) => p.value === value)?.fullEntity;
        if (product?.consumptionUnit) next.consumptionUnit = product.consumptionUnit;
      }
      return next;
    });
  };

  const addProductToList = () => {
    const { productName, quantity, unitPrice, consumptionUnit, packageContent } = currentProduct;
    const content = parseFloat(String(packageContent).replace(',', '.'));

    if (!productName || !quantity || !unitPrice || (isPaintLine && (!consumptionUnit || !(content > 0)))) {
      showSnackbar(t('placeholder.empty_fields'), 'error');
      return;
    }
    if (isPaintLine && !unitAcceptsQuantity(consumptionUnit, content)) {
      showSnackbar(`La contenance en ${unitLabel(consumptionUnit)} doit être un nombre entier`, 'error');
      return;
    }

    const selectedProduct = products.find(
      (product) => product.value === productName
    );


    setProductList((prevList) => [
      ...prevList,
      {
        reference: selectedProduct.reference,
        productName: selectedProduct.name,
        id: selectedProduct.value,
        quantity: parseInt(quantity, 10),
        unitPrice: parseFloat(unitPrice).toFixed(2),
        image: selectedProduct.fullEntity.image,
        ...(isPaintLine && {
          consumptionUnit,
          packageContent: content,
        }),
      },
    ]);

    setCurrentProduct(EMPTY_LINE);
  };

  const removeProductFromList = (index) => {
    setProductList((prevList) => prevList.filter((_, i) => i !== index));
  };

  const handleQuantityChange = (index, newQuantity) => {
    setProductList((prevList) =>
      prevList.map((product, i) =>
        i === index ? { ...product, quantity: Math.max(newQuantity, 0) } : product
      )
    );
  };

  // Uploads a PDF to MinIO and returns the stored object name
  const uploadPdf = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post("/attachments/documents/add", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: localStorage.getItem("token"),
      },
    });
    const key = await getJwkKeystore();
    const data = await getResponseData(response, key);
    if (!data?.fileName) throw new Error("File name not found in the upload response");
    return data.fileName;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (locked) {
      showSnackbar('Cet achat ne peut plus être modifié : sa facture n\'est plus « À vérifier »', 'error');
      return;
    }
    
    // In modify mode, listFields are not required if productList already exists
    const fieldsToValidate = productList.length > 0 
        ? [...baseFields] 
        : [...baseFields, ...listFields];
        
    const missing = fieldsToValidate.filter(f => f.required && !formValues[f.name]
      && (!isModify || EDITABLE_FIELDS.includes(f.name)));
    if (missing.length > 0) {
      showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
      return;
    }
    if (!files.deliveryNote && !isModify) {
      showSnackbar('Veuillez joindre le bon de livraison (PDF)', 'error');
      return;
    }

    setSubmitting(true);
    let deliveryNoteFileName = null;
    let invoiceFileName = null;
    try {
      if (files.deliveryNote) deliveryNoteFileName = await uploadPdf(files.deliveryNote);
      if (files.invoice) invoiceFileName = await uploadPdf(files.invoice);
    } catch (error) {
      console.error('Error uploading purchase attachments:', error.response?.data || error);
      showSnackbar(t('errors.generic'), 'error');
      setSubmitting(false);
      return;
    }

    // Input gives "yyyy-MM-dd"; the backend expects yyyy-MM-dd'T'HH:mm:ssZ
    const purchaseDate = `${formValues.receptionDate}T00:00:00+0000`;

    if (isModify) {
      try {
        // A file left empty keeps the one already attached
        const response = await securePut(`purchases/${initialValues.id}`, {
          purchaseNumber: formValues.factureNumber,
          purchaseDate,
          deliveryNoteFileName,
          invoiceFileName,
        }, showSnackbar);
        onSuccess();
        onSubmit(response.data);
      } catch (error) {
        console.error('Error updating purchase:', error);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const payload = {
      products: productList.map((product) => ({
        productId: product.id,
        price: parseFloat(product.unitPrice),
        quantity: product.quantity,
        ...(product.packageContent != null && {
          consumptionUnit: product.consumptionUnit,
          packageContent: product.packageContent,
        }),
      })),
      providerId: parseInt(formValues.fournisseur, 10),
      purchaseDate,
      purchaseNumber: formValues.factureNumber,
      repoId: parseInt(formValues.stockAlimenter, 10),
      deliveryNoteFileName,
      invoiceFileName,
    };

    try {
      console.log('Submitting payload:', payload);
      const response = await securePut('purchases/add', payload, showSnackbar);
      console.log('Response:', response);

      if (response.status === 200 || response.status === 201) {
        console.log('Purchase added successfully.');
        onSuccess();
        onSubmit(response.data);

      }
    } catch (error) {
      console.error('Error submitting purchase:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageError = (e) => {
    e.target.src = noImage;
    e.target.style.width = "25px";
    e.target.style.height = "25px";
    e.target.style.objectFit = "contain";
  };

  return (

    <div className="w-full">
      <Helmet>
        <title>Drivnest - {t("titles.purchases")}</title>
      </Helmet>

      <div className="flex flex-col space-y-2">
        <Breadcrumbs />
        <div className="flex justify-between items-center">
          <h2 className="text-[32px] font-bold text-black">{title}</h2>
          <SaveCancelTop onCancel={onCancel} onSave={handleSubmit} loading={submitting} />
        </div>
      </div>


      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="mt-4">
          {/* <div className="grid grid-cols- md:grid-cols-4 gap-4"> */}
          <div className="grid grid-cols-3 gap-4 lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-2 xs:grid-cols-1">

            {baseFields.map((field, index) => {
              const disabled = locked || (isModify && !EDITABLE_FIELDS.includes(field.name));
              return (
                <Grayed key={index} active={disabled && field.type !== 'hidden'}>
                  <Input
                    type={field.type || 'text'}
                    label={field.label}
                    name={field.name}
                    value={formValues[field.name] || ''}
                    required={field.required}
                    disabled={disabled}
                    onChange={(value) => handleInputChange(field.name, value)}
                    options={field.options || []}
                  />
                </Grayed>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <PdfDropzone
              name="deliveryNote"
              file={files.deliveryNote}
              existingFile={existingFiles.deliveryNote}
              onChange={(file) => setFiles((prev) => ({ ...prev, deliveryNote: file }))}
              onInvalidFile={() => showSnackbar(t('errors.invalidFileType', { types: 'PDF' }), 'error')}
              placeholderText1="Faites glisser et déposez le bon de livraison (PDF) * ou "
              placeholderText2={t('placeholder.upload_document2')}
            />
            <PdfDropzone
              name="invoice"
              file={files.invoice}
              existingFile={existingFiles.invoice}
              onChange={(file) => setFiles((prev) => ({ ...prev, invoice: file }))}
              onInvalidFile={() => showSnackbar(t('errors.invalidFileType', { types: 'PDF' }), 'error')}
              placeholderText1="Faites glisser et déposez la facture fournisseur (PDF) ou "
              placeholderText2={t('placeholder.upload_document2')}
            />
          </div>
        </div>

        <div>
          <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">{t(`fields_types.products`)}</h3>
          <Grayed active={isModify}>
            {/* Pièces: the 4 fields and the add button on one line; peinture: the 6 fields on a grid */}
            <div className={isPiecesLine
              ? 'flex flex-wrap items-center gap-2'
              : 'grid grid-cols-3 gap-2 lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-2 xs:grid-cols-1'}>
              <Input
                type="dropdown"
                label="Type"
                name="lineType"
                required
                disabled={isModify}
                value={currentProduct.lineType}
                onChange={(value) => setCurrentProduct({ ...EMPTY_LINE, lineType: value || '' })}
                options={LINE_TYPES}
              />
              {currentProduct.lineType && listFields.map((field, index) => (
                <Input
                  key={index}
                  type={field.type || 'text'}
                  label={isPaintLine && field.name === 'quantity' ? 'Quantité (conditionnements)'
                    : isPaintLine && field.name === 'unitPrice' ? "Prix d'achat d'un conditionnement"
                    : field.label}
                  name={field.name}
                  required={field.required}
                  disabled={isModify}
                  value={currentProduct[field.name] || ''}
                  onChange={(value) => handleListFieldChange(field.name, value)}
                  options={
                    field.name === 'productName'
                      ? products.filter((product) => isPeintureProduct(product.fullEntity) === isPaintLine).map((product) => ({
                        value: product.value,
                        name: product.name,
                        reference: product.reference,
                        fullEntity: product.fullEntity,
                      }))
                      : field.options || []
                  }
                />
              ))}
              {isPaintLine && (
                <>
                  <Input
                    type="dropdown"
                    label="Unité de sortie"
                    name="consumptionUnit"
                    required
                    disabled={isModify || !!lockedUnit}
                    value={currentProduct.consumptionUnit || ''}
                    onChange={(value) => handleListFieldChange('consumptionUnit', value)}
                    options={CONSUMPTION_UNITS}
                  />
                  <Input
                    type="text"
                    label={`Contenance d'un conditionnement${currentProduct.consumptionUnit ? ` (${unitLabel(currentProduct.consumptionUnit)})` : ''}`}
                    name="packageContent"
                    required
                    disabled={isModify}
                    value={currentProduct.packageContent || ''}
                    onChange={(value) => handleListFieldChange('packageContent', value)}
                  />
                </>
              )}
              {currentProduct.lineType && (
                <button
                  type="button"
                  onClick={addProductToList}
                  disabled={isModify}
                  className="bg-[#16283E] text-white w-[48px] h-[48px] shrink-0 rounded-[12px] hover:bg-[#3F3456] flex items-center justify-center"
                >
                  <img src={plusIcon} alt="Add" className="w-4 h-4" />
                </button>
              )}
            </div>
          </Grayed>
        </div>

        {productList.length > 0 && (
          <div className="mt-4">
            {/* Header Row */}
            <div className="grid grid-cols-[2fr_1fr_1fr_0.5fr] items-center text-[#919191] text-sm font-semibold mb-2 px-6">
              <div className="text-left">
                <span>{t(`rows.product`)}</span>
              </div>
              <div className="text-center">
                <span>{t(`rows.priceuHT`)}</span>
              </div>
              <div className="text-center">
                <span>{t(`rows.quantity`)}</span>
              </div>
              <div className="text-right">
                <span>{ }</span>
              </div>
            </div>

            {/* Data Rows */}
            {productList.map((product, index) => (
              <div
                key={index}
                className="grid grid-cols-[2fr_1fr_1fr_0.5fr] items-center py-4 px-6 rounded-[12px] bg-white shadow-md mb-2"
              >
                {/* Product Information (Aligned Left) */}
                <div className="flex items-center gap-4 text-left">
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-gray-100 rounded-md overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.productName}
                      className="w-full h-full object-cover"
                      onError={handleImageError}
                    />
                  </div>
                  <div>
                    <p className="text-[#043F88] text-[12px] font-semibold">
                      {product.reference || "Unknown Reference"}
                    </p>
                    <p className="text-[#6B78F8] text-[16px] font-semibold">
                      {product.productName || "Unknown Product"}
                    </p>
                    {product.packageContent != null && (
                      <p className="text-[#707070] text-[12px] font-semibold">
                        Conditionnement de {product.packageContent} {unitLabel(product.consumptionUnit)} ·{' '}
                        {formatAmount(product.unitPrice / product.packageContent)} / {unitLabel(product.consumptionUnit)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Unit Price (Centered) */}
                <div className="text-center text-[#043F88] text-[18px] font-semibold">
                  {formatAmount(product.unitPrice)}
                </div>

                {/* Quantity Selector (Centered) */}
                <div className="flex justify-center">
                  <div className="flex items-stretch rounded-[12px] border border-[#6C78F8] bg-[#6C78F8]/20 divide-x divide-[#6C78F8]">
                    <button
                      type="button"
                      disabled={isModify}
                      onClick={() =>
                        handleQuantityChange(index, Math.max(product.quantity - 1, 1))
                      }
                      className="w-8 h-8 flex items-center justify-center text-[#6C78F8] font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <img src={minus} alt="Minus" className="w-4 h-4" />
                    </button>
                    <span className="min-w-[60px] px-2 h-8 flex items-center justify-center font-semibold text-[#38485C] text-[14px]">
                      {product.quantity}
                    </span>
                    <button
                      type="button"
                      disabled={isModify}
                      onClick={() =>
                        handleQuantityChange(index, product.quantity + 1)
                      }
                      className="w-8 h-8 flex items-center justify-center text-[#6C78F8] font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <img src={plus} alt="Plus" className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Delete Button (Right-Aligned) */}
                <div className="flex justify-end">
                  <button
                    type="button"
                    disabled={isModify}
                    className="w-8 h-8 flex items-center justify-center bg-[#F2F3F3] rounded-[12px] shadow-md cursor-pointer hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => removeProductFromList(index)}
                  >
                    <img src={deleteIcon} alt="Delete" className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </form>
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default PurchaseForm;
